import * as CANNON from 'cannon-es'
import type { LotteryMachineConfig, MachineId } from '../core/types'
import { createMachineLayout, type MachineLayout } from './MachineLayout'

export interface PhysicsHandles {
  world: CANNON.World
  diskInner: CANNON.Body
  diskOuter: CANNON.Body
  gateExit: CANNON.Body
  gatePreview: CANNON.Body
  exitSensorCenter: CANNON.Vec3
  exitSensorHalfExtents: CANNON.Vec3
  chamberRadius: number
  /** 转盘顶面世界 Y */
  diskTopY: number
  layout: MachineLayout
}

/** 直角三角形楔：局部 X 径向向外；直角在外侧贴盘，斜边朝向盘心 */
function createRightTriangleWedge(
  radial: number,
  height: number,
  thickness: number,
): CANNON.ConvexPolyhedron {
  const hz = thickness / 2
  // (0,0)=内侧底，(radial,0)=外侧底直角，(radial,height)=外侧顶
  const vertices = [
    new CANNON.Vec3(0, 0, -hz),
    new CANNON.Vec3(radial, 0, -hz),
    new CANNON.Vec3(radial, height, -hz),
    new CANNON.Vec3(0, 0, hz),
    new CANNON.Vec3(radial, 0, hz),
    new CANNON.Vec3(radial, height, hz),
  ]
  const faces = [
    [0, 1, 2],
    [3, 5, 4],
    [0, 3, 4, 1],
    [1, 4, 5, 2],
    [0, 2, 5, 3],
  ]
  return new CANNON.ConvexPolyhedron({ vertices, faces })
}

export function createPhysicsWorld(
  config: LotteryMachineConfig,
  displaySlots: number,
): PhysicsHandles {
  const world = new CANNON.World({
    gravity: new CANNON.Vec3(0, config.gravityY, 0),
  })
  world.broadphase = new CANNON.SAPBroadphase(world)
  world.allowSleep = false
  ;(world.solver as CANNON.GSSolver).iterations = 24

  const chamberRadius = config.chamberInnerDiameter / 2
  const layout = createMachineLayout(
    {
      chamberInnerDiameter: config.chamberInnerDiameter,
      outerDiskDiameter: config.outerDiskDiameter,
      innerDiskDiameter: config.innerDiskDiameter,
      exitChannelWidth: config.exitChannelWidth,
      baseOuterWidth: config.baseOuterWidth,
      ballRadius: config.ballRadius,
    },
    displaySlots,
  )
  const R = chamberRadius
  const hole = layout.exitHoleRadius
  const exit = layout.exitHoleCenter

  const wallMat = new CANNON.Material('wall')
  const diskMat = new CANNON.Material('disk')
  const ballMat = new CANNON.Material('ball')

  world.addContactMaterial(
    new CANNON.ContactMaterial(ballMat, wallMat, {
      friction: (config.ballFriction + config.wallFriction) / 2,
      restitution: (config.ballRestitution + config.wallRestitution) / 2,
      contactEquationStiffness: 1e7,
      contactEquationRelaxation: 3,
    }),
  )
  world.addContactMaterial(
    new CANNON.ContactMaterial(ballMat, diskMat, {
      friction: Math.min(1.2, (config.ballFriction + config.diskFriction) / 2),
      restitution: 0.05,
      contactEquationStiffness: 1e7,
      contactEquationRelaxation: 3,
      frictionEquationStiffness: 1e7,
      frictionEquationRelaxation: 3,
    }),
  )
  world.addContactMaterial(
    new CANNON.ContactMaterial(ballMat, ballMat, {
      friction: 0.28,
      restitution: 0.32,
      contactEquationStiffness: 1e7,
      contactEquationRelaxation: 3,
    }),
  )

  // 转盘承重面：顶面与视觉盘对齐；侧壁贴盘面球壳
  const diskH = 0.024
  const diskTopY = layout.chamberBottomY + 0.02
  const diskY = diskTopY - diskH / 2

  // 不做竖直侧壁：侧壁顶面会变成「隐形架子」托住球（实测悬空 y≈diskTop+0.12）
  // 水平约束交给 containChamberBalls（球壳截面），承托交给舱底地板

  // 顶部不设实体穹顶挡板：落球管要从顶部进舱，挡板会卡住上层球

  // 舱底地板扇区（跳过出球孔）；贴合玻璃内缘，避免盘外悬空无承托
  const floorR = layout.floorRadius
  const solidR = Math.max(0.06, Math.hypot(exit.x, exit.z) - hole * 1.75)
  const centerFloor = new CANNON.Body({
    mass: 0,
    material: diskMat,
    position: new CANNON.Vec3(0, diskY, 0),
  })
  centerFloor.addShape(new CANNON.Cylinder(solidR, solidR, diskH, 28))
  world.addBody(centerFloor)

  const ringR0 = solidR + 0.004
  const ringR1 = floorR
  const ringMid = (ringR0 + ringR1) / 2
  const ringHalf = (ringR1 - ringR0) / 2
  for (let i = 0; i < 48; i++) {
    const a = (i / 48) * Math.PI * 2
    const px = Math.cos(a) * ringMid
    const pz = Math.sin(a) * ringMid
    if (Math.hypot(px - exit.x, pz - exit.z) < hole * 1.85) continue
    const qx = new CANNON.Quaternion()
    qx.setFromEuler(0, -a, 0)
    const plate = new CANNON.Body({
      mass: 0,
      material: diskMat,
      position: new CANNON.Vec3(0, diskY, 0),
    })
    plate.addShape(
      new CANNON.Box(
        new CANNON.Vec3(ringHalf, diskH / 2, (Math.PI * ringMid) / 48 + 0.01),
      ),
      new CANNON.Vec3(px, 0, pz),
      qx,
    )
    world.addBody(plate)
  }

  // 内外逆向转盘（贴在承重面上方）
  const spinY = diskY + 0.001
  const diskInner = new CANNON.Body({
    mass: 0,
    type: CANNON.Body.KINEMATIC,
    material: diskMat,
    position: new CANNON.Vec3(0, spinY, 0),
  })
  diskInner.addShape(
    new CANNON.Cylinder(layout.innerDiskRadius, layout.innerDiskRadius, diskH * 0.85, 28),
  )
  // 内盘两个直角三角形搅拌楔（加高便于掀球；盘径不改）
  const innerPaddleH = 0.028
  const innerPaddleRadial = 0.024
  const innerPaddleTangential = 0.016
  const innerPaddleR = layout.innerDiskRadius * 0.58
  const innerPaddleLocalY = (diskH * 0.85) / 2 + 0.001
  const innerWedge = createRightTriangleWedge(
    innerPaddleRadial,
    innerPaddleH,
    innerPaddleTangential,
  )
  for (let i = 0; i < 2; i++) {
    const a = (i / 2) * Math.PI * 2
    const qx = new CANNON.Quaternion()
    qx.setFromEuler(0, -a, 0)
    const r0 = innerPaddleR - innerPaddleRadial / 2
    diskInner.addShape(
      innerWedge,
      new CANNON.Vec3(Math.cos(a) * r0, innerPaddleLocalY, Math.sin(a) * r0),
      qx,
    )
  }
  world.addBody(diskInner)

  const diskOuter = new CANNON.Body({
    mass: 0,
    type: CANNON.Body.KINEMATIC,
    material: diskMat,
    position: new CANNON.Vec3(0, spinY, 0),
  })
  const segs = 28
  const midR = (layout.outerDiskInnerR + layout.outerDiskOuterR) / 2
  const halfW = (layout.outerDiskOuterR - layout.outerDiskInnerR) / 2 + 0.01
  for (let i = 0; i < segs; i++) {
    const a = (i / segs) * Math.PI * 2
    const qx = new CANNON.Quaternion()
    qx.setFromEuler(0, -a, 0)
    diskOuter.addShape(
      new CANNON.Box(
        new CANNON.Vec3(halfW, (diskH * 0.85) / 2, (Math.PI * midR) / segs + 0.01),
      ),
      new CANNON.Vec3(Math.cos(a) * midR, 0, Math.sin(a) * midR),
      qx,
    )
  }

  // 外圈四个直角三角形搅拌楔（加高便于掀球；盘径不改）
  const paddleH = 0.03
  const paddleRadial = 0.026
  const paddleTangential = 0.017
  const paddleR = midR
  const paddleLocalY = (diskH * 0.85) / 2 + 0.001
  const outerWedge = createRightTriangleWedge(paddleRadial, paddleH, paddleTangential)
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + Math.PI / 4
    const qx = new CANNON.Quaternion()
    qx.setFromEuler(0, -a, 0)
    const r0 = paddleR - paddleRadial / 2
    diskOuter.addShape(
      outerWedge,
      new CANNON.Vec3(Math.cos(a) * r0, paddleLocalY, Math.sin(a) * r0),
      qx,
    )
  }
  world.addBody(diskOuter)

  const gatePreview = new CANNON.Body({
    mass: 0,
    type: CANNON.Body.KINEMATIC,
    material: wallMat,
    shape: new CANNON.Box(new CANNON.Vec3(R * 0.5, 0.012, 0.1)),
    position: new CANNON.Vec3(
      0,
      layout.feedTubes[0]!.bottomY - 0.01,
      layout.feedTubes[0]!.z,
    ),
  })
  world.addBody(gatePreview)

  // 不再加顶管物理壁：过紧会卡球；视觉管 + 软引导即可

  const gateExit = new CANNON.Body({
    mass: 0,
    type: CANNON.Body.KINEMATIC,
    material: wallMat,
    shape: new CANNON.Cylinder(hole * 1.55, hole * 1.55, diskH + 0.012, 20),
    position: new CANNON.Vec3(exit.x, diskY, exit.z),
  })
  world.addBody(gateExit)

  ;(world as CANNON.World & { __ballMat?: CANNON.Material }).__ballMat = ballMat

  return {
    world,
    diskInner,
    diskOuter,
    gateExit,
    gatePreview,
    exitSensorCenter: layout.exitSensorCenter.clone(),
    exitSensorHalfExtents: layout.exitSensorHalfExtents.clone(),
    chamberRadius,
    diskTopY,
    layout,
  }
}

export function getBallMaterial(world: CANNON.World): CANNON.Material {
  const w = world as CANNON.World & { __ballMat?: CANNON.Material }
  if (!w.__ballMat) w.__ballMat = new CANNON.Material('ball')
  return w.__ballMat
}

export function ballId(machineId: MachineId, number: number): string {
  const prefix = machineId === 'red' ? 'r' : 'b'
  return `ball_${prefix}_${String(number).padStart(2, '0')}`
}
